import React, { createContext, useContext, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { initAnalytics } from '@/lib/analytics'
import { useConsent } from '@/hooks/use-consent'
import { ConsentBanner } from '@/components/consent/ConsentBanner'
import type { ConsentPreferences } from '@/hooks/use-consent'

interface ConsentContextValue {
  hasAnalytics: boolean
  hasErrorTracking: boolean
}

const ConsentContext = createContext<ConsentContextValue>({
  hasAnalytics: false,
  hasErrorTracking: false,
})

export function useConsentContext() {
  return useContext(ConsentContext)
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const { preferences, showBanner, save, acceptAll, rejectAll } = useConsent()
  const analyticsInited = useRef(false)
  const sentryInited = useRef(false)

  useEffect(() => {
    if (preferences?.analytics && !analyticsInited.current) {
      initAnalytics(
        import.meta.env.VITE_POSTHOG_KEY,
        import.meta.env.VITE_POSTHOG_HOST,
      )
      analyticsInited.current = true
    }
  }, [preferences?.analytics])

  useEffect(() => {
    if (preferences?.errorTracking && !sentryInited.current && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: (import.meta.env.VITE_SWIM_ENV as string | undefined) ?? 'development',
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        enabled: true,
        integrations: [Sentry.browserTracingIntegration()],
      })
      sentryInited.current = true
    }
  }, [preferences?.errorTracking])

  const handleSave = (prefs: ConsentPreferences) => save(prefs)

  return (
    <ConsentContext.Provider
      value={{
        hasAnalytics: preferences?.analytics ?? false,
        hasErrorTracking: preferences?.errorTracking ?? false,
      }}
    >
      {children}
      {showBanner && (
        <ConsentBanner
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onSave={handleSave}
        />
      )}
    </ConsentContext.Provider>
  )
}
